'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { Reference } from '@eliadmin/core/src/api/references/references-types';

import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Carousel } from '../ui/carousel';
import { useDisclosure } from '@eliadmin/core/src/hooks/use-disclosure';
import { useState } from 'react';
import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import { LuPlus } from 'react-icons/lu';
import { Button } from '../ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function HeroCarousel({ references }: { references: Reference[] }) {
  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  return (
    <div className="relative w-full md:max-w-[420px] lg:max-w-[652px]">
      <Carousel
        enableLoop
        enableInterval
        intervalLength={4000}
        className={{
          main: 'w-full',
          container: 'w-full',
          wrapper: 'w-full',
          slide: 'h-[382px] w-full sm:h-[480px] md:h-[640px] lg:h-[780px]',
        }}
        slides={references.map((slide, index) => (
          <HeroCarouselSlide key={slide.name} slide={slide as any} index={index} />
        ))}
      />
      <div className="absolute bottom-4 left-4 z-[5] flex items-center justify-start gap-1.5 sm:bottom-6 sm:left-6 lg:bottom-8 lg:left-8">
        {references.map((_, idx) => (
          <span
            key={idx}
            className={cn(
              'block h-2 w-2 rounded-full bg-white/50',
              idx === selectedIndex && 'h-3 w-3 bg-white',
            )}
          />
        ))}
      </div>
    </div>
  );
}

function HeroCarouselSlide({ slide, index }: { slide: Reference; index: number }) {
  const { isOpen, setIsOpen } = useDisclosure(false);
  const [imagesSorted, setImagesSorted] = useState<{ url: string; name: string }[]>([]);

  const { t } = useTranslations();

  const handleOpenGallery = (images: any, index: number) => () => {
    if (Array.isArray(images) && index >= 0 && index < images.length) {
      const newImages = [...images.slice(index), ...images.slice(0, index)];
      setImagesSorted(newImages);
      setIsOpen(true);
    } else if (images && !Array.isArray(images)) {
      setImagesSorted([images]);
      setIsOpen(true);
    }
  };

  const allImages = [...(slide.image ? [slide.image] : []), ...(slide.images || [])];

  return (
    <>
      <LightboxComponent isOpen={isOpen} setIsOpen={setIsOpen} images={imagesSorted} />
      <div className="relative flex h-full w-full items-end justify-start p-4 sm:p-6 lg:p-8">
        {slide.categories && slide.categories.length > 0 && (
          <span className="absolute left-4 top-4 z-10 rounded-full bg-primary-foreground px-4 py-1 text-xs font-normal text-black sm:left-6 sm:top-6 sm:text-sm lg:left-8 lg:top-8">
            {t('text1313')}:&nbsp;<strong className="font-bold">{slide.categories[0].name}</strong>
          </span>
        )}
        {slide.images && slide.images.length > 0 && (
          <Button
            type="button"
            aria-label={`${t('text1944')}: ${slide.name}`}
            size={'icon'}
            variant={'secondary'}
            onClick={handleOpenGallery(allImages, 0)}
            className="absolute right-4 top-4 z-10 cursor-pointer sm:right-6 sm:top-6 lg:right-8 lg:top-8"
          >
            <LuPlus className="h-5 w-5 sm:h-6 sm:w-6" color="black" />
          </Button>
        )}
        <div className="relative z-10 flex flex-col items-start justify-start gap-4 sm:gap-6 lg:gap-8">
          <div className="flex w-full flex-col items-start justify-start gap-4 sm:max-w-[400px]">
            <h2 className="w-full max-w-[360px] text-xl font-bold tracking-tighter text-white sm:max-w-max sm:text-2xl lg:text-3xl">
              {slide.name}
            </h2>
            <p className="text-sm text-white sm:text-base md:text-lg lg:text-xl">{slide.city}</p>
          </div>
          <div className="invisible h-3 w-12" />
        </div>
        <div
          className={cn(
            'absolute inset-0 z-[1] h-full w-full',
            'ea-hero-carousel-slide__image-overlay',
          )}
        />
        <Image
          src={slide.image?.url || '/images/placeholder.png'}
          alt={resolveImageAlt(slide?.image, slide?.name)}
          width={0}
          height={0}
          className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          priority={index === 0}
          loading={index === 0 ? 'eager' : 'lazy'}
          fetchPriority={index === 0 ? 'high' : 'low'}
          sizes="(min-width: 1024px) 652px, (min-width: 768px) 420px, 100vw"
        />
      </div>
    </>
  );
}
